
import { Badge } from "@/components/ui/badge";
import { useEffect, useState } from "react";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

interface CPVCode {
  CPV: number;
  Beskrivelse: string;
}

export const TenderSubscriptions = () => {
  const [preferences, setPreferences] = useState<string[]>([]);
  const [cpvDescriptions, setCpvDescriptions] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const supabase = useSupabaseClient();
  const { i18n } = useTranslation();
  const isDanish = i18n.language === 'da';

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile, error } = await supabase
          .from('profiles')
          .select('preferred_industries')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (profile?.preferred_industries) {
          setPreferences(profile.preferred_industries);
          
          const { data: cpvData, error: cpvError } = await supabase
            .from('CPV Table')
            .select('CPV, Beskrivelse')
            .in('CPV', profile.preferred_industries.map(Number));

          if (cpvError) throw cpvError;

          const descriptions = (cpvData as CPVCode[]).reduce((acc, curr) => ({
            ...acc,
            [curr.CPV.toString()]: curr.Beskrivelse
          }), {});

          setCpvDescriptions(descriptions);
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, [supabase]);

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border flex justify-center items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-4">
          {isDanish ? "Udbudsabonnementer" : "Tender Subscriptions"}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {isDanish 
            ? "Her er de branchekategorier, din udbudsagent overvåger. Du kan administrere disse kategorier i dine profilindstillinger."
            : "Here are the industry categories your tender agent is currently monitoring. You can manage these categories in your profile settings."}
        </p>
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-500 mb-3">
          {isDanish ? "Aktive Kategorier" : "Active Categories"}
        </h3>
        <div className="flex flex-wrap gap-2">
          {preferences.map((cpvCode) => (
            <Badge
              key={cpvCode}
              variant="secondary"
              className="px-3 py-1"
            >
              {cpvDescriptions[cpvCode] || cpvCode}
            </Badge>
          ))}
          {preferences.length === 0 && (
            <p className="text-sm text-gray-500">
              {isDanish 
                ? "Ingen aktive abonnementer fundet. Opdater din profil for at opsætte udbudsovervågning."
                : "No active subscriptions found. Update your profile to set up tender monitoring."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
